/* eslint-disable no-shadow */
import FetchStatus from "@/constants/fetch-status/FetchStatus";
import { httpV2 } from "@/api/index";
import WDService from "@/services/wind/diagnostic/WindDiagnosticService";
import Vuetify from "@/plugins/vuetify";
// Locales.
import { ChartLinesColorsDark, SetLayoutLinesColor } from "@/components/charts/ChartStyles";
import {
	GetRollingBearingFailureResult,
	GetRollingBearingFailureResultError,
	GetRollingBearingFailureResultSuccess,
	SHOW_BPFI,
	SHOW_BPFO,
	SHOW_BSF,
	SHOW_FTF,
	HIDE_BPFI,
	HIDE_BPFO,
	HIDE_BSF,
	HIDE_FTF,
} from "@/stores/actions/RollingBearingFailureDiagnostic";

import SortingService from "@/services/common/SortingService";

const state = {
	bearings:                   {},
	bearingstatus:              FetchStatus.Init,
	data:                       {},
	showBPFI:                   false,
	showBPFO:                   false,
	showFTF:                    false,
	showBSF:                    false,
	shapes:                     [],
	annotations:                [],
	status:                     FetchStatus.Init,
	error:                      {},
	orderLayout:                {},
	frequencyLayout:            {},
	envelopeOrderChartData:     [],
	rawOrderChartData:          [],
	envelopeFrequencyChartData: [],
	rawFrequencyChartData:      [],
	options:                    {
		responsive:     true,
		displayModeBar: false,
		showLink:       false,
		displayLogo:    false,
	},
};

const Qs = require("qs");

/**
 * @param {String} failMode Nome do modo de falha.
 * @returns {String} index:
 * 0 - LightningBlue,
 * 1 - Purple,
 * 2 - Orange,
 * 3- Brown.
 */
const GetShapesColor = (failMode) =>
{
	switch (failMode)
	{
	case "BPFO": return ChartLinesColorsDark[0];
	case "BPFI": return ChartLinesColorsDark[1];
	case "BSF": return ChartLinesColorsDark[2];
	case "FTF": return ChartLinesColorsDark[3];
	default: return "";
	}
};

/**
 * @summary Cria as anotações e atribui na váriavel layout.
 * @param {Array} orders Array com as posiçoes do eixo x das anotações.
 */
const CreateAnnotations = (orders, failMode) =>
{
	const shapeColor = GetShapesColor(failMode);

	const shapes = [];
	for (let i = 0; i < orders.length; i++)
	{
		const shape = {
			yref: "paper",
			type: "line",
			x0:   orders[i],
			x1:   orders[i],
			y0:   0, // Representa 0%
			y1:   1, // Representa 100%
			line: {
				color: shapeColor,
				width: 2,
				dash:  "dot",
			},
			visible: false,
		};
		shapes.push(shape);
	}
	return shapes;
};

/**
	* @summary Cria os textos para as anotações
	* @param {Array - Numbers} orders - Posições das anotações no eixo Y.
	* @param {String} failMode - Nome do modo de falha.
*/
const CreateAnnotationText = (orders, failMode, darkMode) =>
{
	const annotations = [];
	for (let i = 0; i < orders.length; i++)
	{
		// Seleciona a cor do texto
		const textColor = darkMode ? Vuetify.framework.theme.themes.dark.secondary :
			Vuetify.framework.theme.themes.light.secondary;

		// Cria o texto
		let text = "";
		switch (failMode)
		{
		case "BPFO": text = `${i + 1}x BPFO`; break;
		case "BPFI": text = `${i + 1}x BPFI`; break;
		case "BSF": text = `${i + 1}x BSF`; break;
		case "FTF": text = `${i + 1}x FTF`; break;
		default: text = ""; break;
		}

		// Cria a anotação em texto.
		const annotation = {
			x:          orders[i],
			y:          0.8, // 80 % do valor em Y
			yref:       "paper",
			mode:       "text",
			showlegend: false,
			text,
			showarrow:  false,
			visible:    false,
			textangle:  270,
			align:      "right",
			yanchor:    "top",
			xshift:     10,
			yshift:     30,
			font:       { color: textColor },
		};
		annotations.push(annotation);
	}
	return annotations;
};

const actions = {
	/**
	 * Request na api para obter os resultados de mancais de rolamento.
	 * @param {Object} commit Commit de efetivação do state.
	 * @param {Array} requestParams Parâmetros necessários para a api.
	 */
	[GetRollingBearingFailureResult]: ({ commit }, parameters) =>
	{
		commit(GetRollingBearingFailureResult);
		httpV2.get(`/WindTurbinesDiagnostic/${parameters.resultSetId}/RollingBearingsChart`, {
			params:           parameters,
			paramsSerializer: params => Qs.stringify(params,
				{ encode: false }),
		 })
			.then((resp) =>
			{
				SortingService.SortListAlphabeticallyByName(resp.data.envelopeChart);

				SortingService.SortListAlphabeticallyByName(resp.data.rawChart);

				const rawOrderChartData = WDService.CreateChart(resp.data.rawChart, true);
				const envelopeOrderChartData = WDService.CreateChart(resp.data.envelopeChart, true);

				const rawFrequencyChartData = WDService.CreateChart(resp.data.rawChart, false);
				const envelopeFrequencyChartData = WDService.CreateChart(resp.data.envelopeChart, false);

				const bpfiOrderShapes = CreateAnnotations(resp.data.concernOrders.bpfi, "BPFI");
				const bpfoOrderShapes = CreateAnnotations(resp.data.concernOrders.bpfo, "BPFO");
				const bsfOrderShapes = CreateAnnotations(resp.data.concernOrders.bsf, "BSF");
				const ftfOrderShapes = CreateAnnotations(resp.data.concernOrders.ftf, "FTF");
				const bpfiOrderAnnotations = CreateAnnotationText(resp.data.concernOrders.bpfi, "BPFI", parameters.darkMode);
				const bpfoOrderAnnotations = CreateAnnotationText(resp.data.concernOrders.bpfo, "BPFO", parameters.darkMode);
				const bsfOrderAnnotations = CreateAnnotationText(resp.data.concernOrders.bsf, "BSF", parameters.darkMode);
				const ftfOrderAnnotations = CreateAnnotationText(resp.data.concernOrders.ftf, "FTF", parameters.darkMode);

				const bpfiFrequencyShapes = CreateAnnotations(resp.data.concernFrequencies.bpfi, "BPFI");
				const bpfoFrequencyShapes = CreateAnnotations(resp.data.concernFrequencies.bpfo, "BPFO");
				const bsfFrequencyShapes = CreateAnnotations(resp.data.concernFrequencies.bsf, "BSF");
				const ftfFrequencyShapes = CreateAnnotations(resp.data.concernFrequencies.ftf, "FTF");

				const bpfiFrequencyAnnotations =
					CreateAnnotationText(resp.data.concernFrequencies.bpfi, "BPFI", parameters.darkMode);

				const bpfoFrequencyAnnotations =
					CreateAnnotationText(resp.data.concernFrequencies.bpfo, "BPFO", parameters.darkMode);

				const bsfFrequencyAnnotations =
					CreateAnnotationText(resp.data.concernFrequencies.bsf, "BSF", parameters.darkMode);

				const ftfFrequencyAnnotations =
					CreateAnnotationText(resp.data.concernFrequencies.ftf, "FTF", parameters.darkMode);

				const orderShapes = bpfiOrderShapes.concat(bpfoOrderShapes, bsfOrderShapes, ftfOrderShapes);
				const frequenciesAnnotations = bpfiFrequencyAnnotations
					.concat(bpfoFrequencyAnnotations, bsfFrequencyAnnotations, ftfFrequencyAnnotations);
				const frequenciesShapes = bpfiFrequencyShapes
					.concat(bpfoFrequencyShapes, bsfFrequencyShapes, ftfFrequencyShapes);
				const orderAnnotations = bpfiOrderAnnotations
					.concat(bpfoOrderAnnotations, bsfOrderAnnotations, ftfOrderAnnotations);

				const orderLayout =
					SetLayoutLinesColor(WDService.OrderLayoutModel(), parameters.darkMode);

				const frequencyLayout =
					SetLayoutLinesColor(WDService.FrequencyLayoutModel(), parameters.darkMode);

				const chartParameters = {
					orderLayout,
					frequencyLayout,
					envelopeOrderChartData,
					rawOrderChartData,
					envelopeFrequencyChartData,
					rawFrequencyChartData,
					orderShapes,
					orderAnnotations,
					frequenciesShapes,
					frequenciesAnnotations,
				};

				commit(GetRollingBearingFailureResultSuccess, chartParameters);
			})
			.catch((error) =>
			{
				commit(GetRollingBearingFailureResultError, error.response);
			});
	},

	[SHOW_BPFI]: ({ commit }, failModeName) =>
	{
		commit(SHOW_BPFI, failModeName);
	},
	[SHOW_BPFO]: ({ commit }, failModeName) =>
	{
		commit(SHOW_BPFO, failModeName);
	},
	[SHOW_BSF]: ({ commit }, failModeName) =>
	{
		commit(SHOW_BSF, failModeName);
	},
	[SHOW_FTF]: ({ commit }, failModeName) =>
	{
		commit(SHOW_FTF, failModeName);
	},
	[HIDE_BPFI]: ({ commit }, failModeName) =>
	{
		commit(HIDE_BPFI, failModeName);
	},
	[HIDE_BPFO]: ({ commit }, failModeName) =>
	{
		commit(HIDE_BPFO, failModeName);
	},
	[HIDE_BSF]: ({ commit }, failModeName) =>
	{
		commit(HIDE_BSF, failModeName);
	},
	[HIDE_FTF]: ({ commit }, failModeName) =>
	{
		commit(HIDE_FTF, failModeName);
	},
};

const mutations = {
	[GetRollingBearingFailureResult]: (state) =>
	{
		state.status = FetchStatus.Loading;
	},

	[GetRollingBearingFailureResultSuccess]: (state, resp) =>
	{
		state.data = resp;

		state.orderLayout = resp.orderLayout;
		state.orderLayout.shapes = resp.orderShapes;
		state.orderLayout.annotations = resp.orderAnnotations;

		state.frequencyLayout = resp.frequencyLayout;
		state.frequencyLayout.shapes = resp.frequenciesShapes;
		state.frequencyLayout.annotations = resp.frequenciesAnnotations;

		state.envelopeOrderChartData = resp.envelopeOrderChartData;
		state.rawOrderChartData = resp.rawOrderChartData;
		state.envelopeFrequencyChartData = resp.envelopeFrequencyChartData;
		state.rawFrequencyChartData = resp.rawFrequencyChartData;
		state.status = FetchStatus.Loaded;
	},

	[GetRollingBearingFailureResultError]: (state, error) =>
	{
		state.error = error.response;
		state.status = FetchStatus.Error;
	},

	[SHOW_BPFO]: (state, failModeName) =>
	{
		state.showBPFO = true;
		for (let i = 0; i < state.orderLayout.shapes.length; i++)
		{
			if (state.orderLayout.annotations[i].text.includes(failModeName))
			{
				state.frequencyLayout.shapes[i].visible = true;
				state.frequencyLayout.annotations[i].visible = true;
				state.orderLayout.shapes[i].visible = true;
				state.orderLayout.annotations[i].visible = true;
			}
		}
		state.orderLayout.yaxis.showgrid = false;
		state.orderLayout.xaxis.showgrid = false;
		state.frequencyLayout.yaxis.showgrid = false;
		state.frequencyLayout.xaxis.showgrid = false;
	},
	[SHOW_BPFI]: (state, failModeName) =>
	{
		state.showBPFI = true;
		for (let i = 0; i < state.orderLayout.shapes.length; i++)
		{
			if (state.orderLayout.annotations[i].text.includes(failModeName))
			{
				state.frequencyLayout.shapes[i].visible = true;
				state.frequencyLayout.annotations[i].visible = true;
				state.orderLayout.shapes[i].visible = true;
				state.orderLayout.annotations[i].visible = true;
			}
			state.orderLayout.yaxis.showgrid = false;
			state.orderLayout.xaxis.showgrid = false;
			state.frequencyLayout.yaxis.showgrid = false;
			state.frequencyLayout.xaxis.showgrid = false;
		}
	},
	[SHOW_BSF]: (state, failModeName) =>
	{
		state.showBSF = true;
		for (let i = 0; i < state.orderLayout.shapes.length; i++)
		{
			if (state.orderLayout.annotations[i].text.includes(failModeName))
			{
				state.frequencyLayout.shapes[i].visible = true;
				state.frequencyLayout.annotations[i].visible = true;
				state.orderLayout.shapes[i].visible = true;
				state.orderLayout.annotations[i].visible = true;
			}
			state.orderLayout.yaxis.showgrid = false;
			state.orderLayout.xaxis.showgrid = false;
			state.frequencyLayout.yaxis.showgrid = false;
			state.frequencyLayout.xaxis.showgrid = false;
		}
	},
	[SHOW_FTF]: (state, failModeName) =>
	{
		state.showFTF = true;
		for (let i = 0; i < state.orderLayout.shapes.length; i++)
		{
			if (state.orderLayout.annotations[i].text.includes(failModeName))
			{
				state.frequencyLayout.shapes[i].visible = true;
				state.frequencyLayout.annotations[i].visible = true;
				state.orderLayout.shapes[i].visible = true;
				state.orderLayout.annotations[i].visible = true;
			}
			state.orderLayout.yaxis.showgrid = false;
			state.orderLayout.xaxis.showgrid = false;
			state.frequencyLayout.yaxis.showgrid = false;
			state.frequencyLayout.xaxis.showgrid = false;
		}
	},
	[HIDE_BPFO]: (state, failModeName) =>
	{
		state.showBPFO = false;

		for (let i = 0; i < state.orderLayout.shapes.length; i++)
		{
			if (state.orderLayout.annotations[i].text.includes(failModeName))
			{
				state.orderLayout.shapes[i].visible = false;
				state.orderLayout.annotations[i].visible = false;
				state.frequencyLayout.shapes[i].visible = false;
				state.frequencyLayout.annotations[i].visible = false;
			}
		}
		if (!state.showBPFI && !state.showBPFO && !state.showFTF && !state.showBSF)
		{
			state.orderLayout.yaxis.showgrid = true;
			state.orderLayout.xaxis.showgrid = true;
			state.frequencyLayout.yaxis.showgrid = true;
			state.frequencyLayout.xaxis.showgrid = true;
		}
	},
	[HIDE_BPFI]: (state, failModeName) =>
	{
		state.showBPFI = false;

		for (let i = 0; i < state.orderLayout.shapes.length; i++)
		{
			if (state.orderLayout.annotations[i].text.includes(failModeName))
			{
				state.orderLayout.shapes[i].visible = false;
				state.orderLayout.annotations[i].visible = false;
				state.frequencyLayout.shapes[i].visible = false;
				state.frequencyLayout.annotations[i].visible = false;
			}
		}
		if (!state.showBPFI && !state.showBPFO && !state.showFTF && !state.showBSF)
		{
			state.orderLayout.yaxis.showgrid = true;
			state.orderLayout.xaxis.showgrid = true;
			state.frequencyLayout.yaxis.showgrid = true;
			state.frequencyLayout.xaxis.showgrid = true;
		}
	},
	[HIDE_BSF]: (state, failModeName) =>
	{
		state.showBSF = false;

		for (let i = 0; i < state.orderLayout.shapes.length; i++)
		{
			if (state.orderLayout.annotations[i].text.includes(failModeName))
			{
				state.orderLayout.shapes[i].visible = false;
				state.orderLayout.annotations[i].visible = false;
				state.frequencyLayout.shapes[i].visible = false;
				state.frequencyLayout.annotations[i].visible = false;
			}
		}
		if (!state.showBPFI && !state.showBPFO && !state.showFTF && !state.showBSF)
		{
			state.orderLayout.yaxis.showgrid = true;
			state.orderLayout.xaxis.showgrid = true;
			state.frequencyLayout.yaxis.showgrid = true;
			state.frequencyLayout.xaxis.showgrid = true;
		}
	},
	[HIDE_FTF]: (state, failModeName) =>
	{
		state.showFTF = false;

		for (let i = 0; i < state.orderLayout.shapes.length; i++)
		{
			if (state.orderLayout.annotations[i].text.includes(failModeName))
			{
				state.orderLayout.shapes[i].visible = false;
				state.orderLayout.annotations[i].visible = false;
				state.frequencyLayout.shapes[i].visible = false;
				state.frequencyLayout.annotations[i].visible = false;
			}
		}
		if (!state.showBPFI && !state.showBPFO && !state.showFTF && !state.showBSF)
		{
			state.orderLayout.yaxis.showgrid = true;
			state.orderLayout.xaxis.showgrid = true;
			state.frequencyLayout.yaxis.showgrid = true;
			state.frequencyLayout.xaxis.showgrid = true;
		}
	},
};

export default {
	state,
	actions,
	mutations,
};
